// Mirrored verbatim in TempMee/mobile-app — change both together.

import {
  getTimePerPatientKey,
  isParkingAsked,
  isParkingCostType,
} from '../rules/getMissingOfficeInfoFields';
import type {
  IOfficeMissingInfoFormValues,
  IOfficeMissingInfoParking,
  IOfficeMissingInfoSource,
  TMissingOfficeInfoField,
} from '../types/OfficeMissingInfo';
import type {
  IOfficeMissingInfoCredentialsPatch,
  IOfficeMissingInfoProductivityPatch,
  IOfficeMissingInfoUsersPatch,
} from '../types/OfficeMissingInfo.api';

/** Re-sent when the office has no specialty, as Account Information does. */
export const DEFAULT_OFFICE_SPECIALTY = 'specialty_General Dentistry';

export interface IOfficeMissingInfoPatches {
  usersPatch?: IOfficeMissingInfoUsersPatch;
  productivityPatch?: IOfficeMissingInfoProductivityPatch;
  credentialsPatch?: IOfficeMissingInfoCredentialsPatch;
}

export interface IToOfficeMissingInfoPatchesArgs {
  values: IOfficeMissingInfoFormValues;
  missingFields: TMissingOfficeInfoField[];
  /** Reduced profession code (EFDA → DA). */
  professionCode: string;
  source: IOfficeMissingInfoSource;
}

type TParkingPatch = NonNullable<IOfficeMissingInfoUsersPatch['parkingInformation']>;

/**
 * The server replaces `parkingInformation` whole, so every key is the answered value or, when it
 * wasn't asked, the stored one. Answering "not free" always asks for both cost and unit.
 */
const toParkingPatch = (
  values: IOfficeMissingInfoFormValues,
  isAsked: (field: TMissingOfficeInfoField) => boolean,
  current?: IOfficeMissingInfoParking | null,
): TParkingPatch => {
  const parkingType = (isAsked('parkingType') ? values.parkingType : current?.parkingType) ?? '';
  const isParkingFree = isAsked('parkingCost')
    ? values.isParkingFree === true
    : current?.isParkingFree === true;

  if (isParkingFree) {
    return { isParkingFree: true, parkingType };
  }

  const cost = isAsked('parkingCost') ? Number(values.parkingCost) : (current?.cost ?? undefined);
  const costType =
    isAsked('parkingCost') || isAsked('parkingCostType')
      ? values.parkingCostType
      : current?.costType;

  return {
    isParkingFree: false,
    cost,
    costType: isParkingCostType(costType) ? costType : undefined,
    parkingType,
  };
};

/** Form values → the partial writes, each built only when one of its fields was asked. */
const toPatches = ({
  values,
  missingFields,
  professionCode,
  source,
}: IToOfficeMissingInfoPatchesArgs): IOfficeMissingInfoPatches => {
  const isAsked = (field: TMissingOfficeInfoField) => missingFields.includes(field);
  const patches: IOfficeMissingInfoPatches = {};

  const officeInfo: NonNullable<IOfficeMissingInfoUsersPatch['office_info']> = {};
  const timePerPatientKey = getTimePerPatientKey(professionCode);
  if (isAsked('timePerPatient') && timePerPatientKey) {
    officeInfo[timePerPatientKey] = values.timePerPatient;
  }
  if (isAsked('assistedHygiene')) {
    officeInfo.isAssistedHygiene = values.isAssistedHygiene === true;
  }

  const parkingAsked = isParkingAsked(missingFields);

  if (Object.keys(officeInfo).length > 0 || parkingAsked) {
    patches.usersPatch = {
      ...(Object.keys(officeInfo).length > 0 && { office_info: officeInfo }),
      ...(parkingAsked && {
        parkingInformation: toParkingPatch(values, isAsked, source.parkingInformation),
      }),
    };
  }

  if (isAsked('operatories')) {
    patches.productivityPatch = { productivity: { operatories: values.operatories } };
  }

  if (isAsked('softwares') || isAsked('insurances')) {
    const specialtyKeys = source.specialtyKeys?.length
      ? source.specialtyKeys
      : [DEFAULT_OFFICE_SPECIALTY];

    patches.credentialsPatch = {
      education: {
        specialtiesAndProcedures: Object.fromEntries(
          specialtyKeys.map((key): [string, string[]] => [key, []]),
        ),
        softwares: isAsked('softwares') ? values.softwares : (source.softwares ?? []),
      },
      insurances: isAsked('insurances') ? values.insurances : (source.insurances ?? []),
    };
  }

  return patches;
};

export const OfficeMissingInfoAdapters = { toPatches };
